import { useLocale, useT } from "../../i18n";
import { formatMileage } from "../../lib/format";
import { labelOf, useTaxonomies } from "../../lib/taxonomies";
import { ResponsiveImage } from "../ResponsiveImage";

export const DetailSection = ({ id, index, title, children }) => (
  <section data-testid={`detail-section-${id}`} className="grid gap-8 border-t border-ink/15 py-14 lg:grid-cols-12 lg:gap-10 lg:py-20">
    <div className="lg:col-span-4">
      <p className="micro-label tabular-nums text-ink/40">{index}</p>
      <h2 className="font-display mt-3 text-3xl leading-tight lg:text-4xl">{title}</h2>
    </div>
    <div className="lg:col-span-8">{children}</div>
  </section>
);

export const specRows = (v, t, tx) => {
  const d = v.dimensions || {};
  const en = (group, key) => key && t(`enums.${group}.${key}`);
  const tl = (list, group, key) => key && (labelOf(tx, list, key) || en(group, key));
  const color = (side) => v[`${side}_color_name`] || labelOf(tx, "colors", v[`${side}_color_id`]) || v[`${side}_color`];
  return [
    ["labels.stockId", v.stock_id],
    ["labels.modelYear", v.model_year],
    ["labels.registrationYear", v.registration_year],
    ["labels.mileage", formatMileage(v.mileage_km)],
    ["labels.bodyType", tl("body_types", "body_type", v.body_type)],
    ["labels.fuel", tl("fuel_types", "fuel_type", v.fuel_type)],
    ["labels.transmission", tl("transmissions", "transmission", v.transmission)],
    ["labels.drivetrain", tl("drivetrains", "drivetrain", v.drivetrain)],
    ["labels.aspiration", labelOf(tx, "aspirations", v.aspiration)],
    ["labels.steering", en("steering", v.steering)],
    ["labels.engine", v.engine],
    ["labels.displacement", v.displacement_cc && `${v.displacement_cc.toLocaleString("en-US")} cc`],
    ["labels.modelCode", v.model_code],
    ["labels.doors", v.doors],
    ["labels.seats", v.seats],
    ["labels.exteriorColor", color("exterior")],
    ["labels.interiorColor", color("interior")],
    ["labels.dimensions", d.length && d.width && d.height && `${d.length.toLocaleString("en-US")} × ${d.width.toLocaleString("en-US")} × ${d.height.toLocaleString("en-US")} mm`],
    ["labels.shakenExpiry", v.shaken_expiry],
    ["labels.repairHistory", v.repair_history !== "unknown" && en("repair_history", v.repair_history)],
  ].filter(([, val]) => val !== null && val !== undefined && val !== "" && val !== false);
};

export const SpecsList = ({ rows }) => {
  const t = useT();
  return (
    <dl className="grid border-t border-ink/10 sm:grid-cols-2 sm:gap-x-10" data-testid="detail-specs-list">
      {rows.map(([label, value]) => (
        <div key={label} className="grid grid-cols-[minmax(0,11rem)_1fr] gap-4 border-b border-ink/10 py-3.5 text-[15px]">
          <dt className="text-ink/50">{t(label)}</dt>
          <dd className="tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

// Only the selected equipment, grouped by the canonical equipment groups.
export const FeaturesList = ({ keys }) => {
  const [tx] = useTaxonomies();
  if (!tx) return null;
  const legacy = tx.legacy_equipment || {};
  const picked = new Set(keys.map((k) => legacy[k] || k));
  return (
    <div className="grid gap-10 sm:grid-cols-2 xl:grid-cols-3" data-testid="detail-features-list">
      {tx.equipment_groups.map((g) => {
        const items = tx.equipment.filter((e) => e.group === g.key && picked.has(e.key));
        if (!items.length) return null;
        return (
          <div key={g.key} data-testid={`detail-equipment-group-${g.key}`}>
            <h3 className="micro-label mb-2 text-ink/45">{g.label}</h3>
            <ul>{items.map((e) => <li key={e.key} className="border-b border-ink/10 py-3 text-[15px]">{e.label}</li>)}</ul>
          </div>
        );
      })}
    </div>
  );
};

const GRADE_KEYS = ["exterior", "interior", "mechanical", "wheels", "tires"];

export const hasCondition = (c) =>
  !!c && (GRADE_KEYS.some((k) => c[k]) || c.known_imperfections?.length || c.notes_en || c.notes_ja);

export const ConditionPanel = ({ condition: c, images, onOpenPhoto }) => {
  const t = useT();
  const lang = useLocale();
  const grades = GRADE_KEYS.filter((k) => c[k]);
  const notes = lang === "ja" ? c.notes_ja || c.notes_en : c.notes_en || c.notes_ja;
  return (
    <div className="grid gap-12" data-testid="detail-condition">
      {grades.length > 0 && (
        <dl className="grid grid-cols-2 border-l border-t border-ink/10 sm:grid-cols-5">
          {grades.map((k) => (
            <div key={k} className="border-b border-r border-ink/10 px-4 py-4">
              <dt className="micro-label text-ink/45">{t(`detail.conditionLabels.${k}`)}</dt>
              <dd className="mt-2 text-[15px]">{t(`enums.condition_grade.${c[k]}`)}</dd>
            </div>
          ))}
        </dl>
      )}
      {c.known_imperfections?.length > 0 && (
        <div data-testid="detail-imperfections">
          <h3 className="micro-label mb-4 text-ink/70">{t("detail.knownImperfections")}</h3>
          <ul className="border-t border-ink/10">
            {c.known_imperfections.map((imp, i) => (
              <li key={i} className="flex flex-col gap-4 border-b border-ink/10 py-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-[15px]">{(lang === "ja" ? imp.note_ja || imp.note_en : imp.note_en || imp.note_ja)}</p>
                <div className="flex shrink-0 gap-2">
                  {imp.photo_ids.map((pid) => {
                    const idx = images.findIndex((im) => im.id === pid);
                    if (idx < 0) return null;
                    return (
                      <button key={pid} type="button" onClick={() => onOpenPhoto(idx)} aria-label={`${t("detail.viewPhoto")}: ${images[idx].alt}`} data-testid={`imperfection-photo-${pid}`} className="relative h-16 w-24 overflow-hidden bg-mist transition-opacity duration-300 hover:opacity-80">
                        <ResponsiveImage image={images[idx]} maxVariant="thumb" sizes="96px" className="h-full w-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {notes && <p className="max-w-2xl text-[15px] leading-relaxed text-ink/75">{notes}</p>}
      <p className="text-xs text-ink/45" data-testid="condition-disclaimer">
        {c.inspected_by_admin ? t("detail.inspected") : t("detail.conditionDisclaimer")}
      </p>
    </div>
  );
};
